// CostIntelligence — wedge workspace: pipeline upload, recipe-cost explorer and
// menu engineering.
//
// Endpoints used:
//   POST /api/documents/bulk (via PipelineBulkUpload) — canonical invoice intake;
//        review happens at /review
//   GET  /api/menu-items                              — list dishes
//   GET  /api/costing/ingredients                     — list ingredients (org-scoped)
//   POST /api/costing/recipes/{id}/cost               — recipe-DAG cost compute
//   GET  /api/costing/menu-engineering                — menu engineering quadrants
//   GET  /api/costing/menu-engineering/landed         — landed vs raw (LandedVsRaw)
//   GET  /api/recost-alerts                           — recost alert feed (RecostAlerts)
//
// The legacy CSV import (/api/costing/import/*) and the HITL ingestion tab
// (/api/ingestion/jobs) were removed on 2026-09-30: both backends return 410
// Gone unless an emergency-rollback env flag is set, and the tabs that called
// them were no longer rendered.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChefHat, TrendingUp, AlertTriangle,
  Loader2, PoundSterling, Sprout, BarChart3, RefreshCw
} from 'lucide-react';
import PipelineBulkUpload from './PipelineBulkUpload';
import RecostAlerts from './RecostAlerts';
import LandedVsRaw from './LandedVsRaw';
import { fmtUnitCost } from '../utils/unitCost';
import { formatMoney } from '../utils/money';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (Number.isNaN(n)) return String(v);
  return formatMoney(n);
};

export { fmtUnitCost } from '../utils/unitCost';

const confidencePill = (c) =>
  c === 'high' ? 'bg-ok/10 text-ok border-ok/30'
  : c === 'medium' ? 'bg-warn/10 text-warn border-warn/30'
  : 'bg-danger/10 text-danger border-danger/30';

const fmtPct = (v, digits = 1) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (Number.isNaN(n)) return String(v);
  return `${(n * 100).toFixed(digits)}%`;
};

const quadrantMeta = {
  star: { label: 'Stars', tone: 'bg-ok/10 text-ok border-ok/30' },
  workhorse: { label: 'Workhorses', tone: 'bg-warn/10 text-warn border-warn/30' },
  puzzle: { label: 'Puzzles', tone: 'bg-primary-50 text-primary-700 border-primary-500/30' },
  dog: { label: 'Dogs', tone: 'bg-danger/10 text-danger border-danger/30' },
  unclassified: { label: 'Unclassified', tone: 'bg-surface-sunken text-ink-muted border-surface-border' },
};

const Tab = ({ id, active, onClick, children, testid }) => (
  <button
    onClick={() => onClick(id)}
    className={`px-4 py-2 text-sm font-medium border-b-2 motion-organic ${
      active === id
        ? 'border-primary-500 text-primary-700'
        : 'border-transparent text-ink-muted hover:text-primary-500'
    }`}
    data-testid={testid}
  >
    {children}
  </button>
);

// Resolve the active org id from /api/platform/context (mirrors the Navbar pattern).
// Returned headers always include X-Organization-Id once known.
const useOrgScopedHeaders = (token) => {
  const [orgId, setOrgId] = useState(() => {
    try { return localStorage.getItem('current_organization_id') || null; } catch { return null; }
  });
  const [locationId, setLocationId] = useState(() => {
    try { return localStorage.getItem('current_location_id') || null; } catch { return null; }
  });

  useEffect(() => {
    const syncLocation = (event) => {
      const selected = event?.detail?.id;
      try { setLocationId(selected || localStorage.getItem('current_location_id') || null); }
      catch { setLocationId(selected || null); }
    };
    window.addEventListener('locationChanged', syncLocation);
    return () => window.removeEventListener('locationChanged', syncLocation);
  }, []);

  useEffect(() => {
    if (!token || orgId) return;
    let cancelled = false;
    fetch(`${API_URL}/api/platform/context`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(ctx => {
        if (cancelled || !ctx) return;
        const id = ctx?.organizations?.[0]?.id;
        if (id) {
          setOrgId(id);
          try { localStorage.setItem('current_organization_id', id); } catch { /* ignore */ }
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token, orgId]);

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(orgId ? { 'X-Organization-Id': orgId } : {}),
    ...(locationId ? { 'X-Location-Id': locationId } : {}),
  }), [token, orgId, locationId]);

  return { headers, orgId };
};

/** £ recovered headline — Sellability S2.5 */
const RecoveredHeadline = () => {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  const { headers, orgId } = useOrgScopedHeaders(token);
  const [recovered, setRecovered] = useState(null);

  useEffect(() => {
    if (!orgId || !token) return;
    let cancelled = false;
    fetch(`${API_URL}/api/roi/recovered`, { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data) setRecovered(data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [headers, orgId, token]);

  if (!recovered?.headline) return null;
  return (
    <div
      className="text-right max-w-xs p-3 rounded-lg border border-ok/30 bg-ok/10"
      data-testid="cost-intel-recovered"
    >
      <div className="text-lg font-semibold text-ok">{recovered.headline}</div>
      <div className="text-xs text-ink-muted mt-1">Raise-price + supplier catches (estimate)</div>
    </div>
  );
};

const CostIntelligence = () => {
  const [tab, setTab] = useState('pipeline');
  return (
    <div className="min-h-screen bg-surface-muted" data-testid="cost-intel-page">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-overline text-ink-muted">Cut Cost</div>
            <h1 className="font-display text-3xl text-ink mt-1 flex items-center gap-2">
              <Sprout className="w-7 h-7 text-primary-500" />
              Cost Intelligence
            </h1>
            <p className="text-ink-muted text-sm mt-1">Recipe-DAG cost explorer with provenance, plus the AI-ingestion human-in-loop review queue.</p>
          </div>
          <RecoveredHeadline />
        </div>

        <div className="border-b border-surface-border mb-6 flex gap-1">
          <Tab id="pipeline"  active={tab} onClick={setTab} testid="cost-intel-tab-pipeline">Pipeline upload</Tab>
          <Tab id="explorer"  active={tab} onClick={setTab} testid="cost-intel-tab-explorer">Recipe Explorer</Tab>
          <Tab id="engineering" active={tab} onClick={setTab} testid="cost-intel-tab-engineering">Menu Engineering</Tab>
          <Tab id="landed" active={tab} onClick={setTab} testid="cost-intel-tab-landed">Landed vs raw</Tab>
          <Tab id="alerts" active={tab} onClick={setTab} testid="cost-intel-tab-alerts">Recost alerts</Tab>
        </div>

        {tab === 'pipeline' && (
          <div className="space-y-4" data-testid="cost-intel-pipeline-panel">
            <PipelineBulkUpload />
            <p className="text-xs text-ink-muted">
              Canonical intake: <code className="text-[11px]">POST /api/documents/bulk</code> then
              {' '}<a className="underline" href="/review">/review</a>. Legacy CSV/ingestion tabs were removed.
            </p>
          </div>
        )}
        {tab === 'explorer'  && <RecipeExplorer />}
        {tab === 'engineering' && <MenuEngineering />}
        {tab === 'landed' && <LandedVsRaw />}
        {tab === 'alerts' && <RecostAlerts />}
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Recipe Explorer
// ───────────────────────────────────────────────────────────────────────────
const RecipeExplorer = () => {
  const [items, setItems] = useState([]);
  const [ingredients, setIngredients] = useState({});
  const [selected, setSelected] = useState(null);
  const [costResult, setCostResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [computing, setComputing] = useState(false);
  const [sellingPrice, setSellingPrice] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const { headers: authHeaders, orgId } = useOrgScopedHeaders(token);

  const load = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [miRes, ingRes] = await Promise.all([
        fetch(`${API_URL}/api/menu-items`, { headers: authHeaders }),
        fetch(`${API_URL}/api/costing/ingredients`, { headers: authHeaders }),
      ]);
      const mi = miRes.ok ? await miRes.json() : [];
      const ing = ingRes.ok ? await ingRes.json() : [];
      // API returns `base_price`; normalise to `price` so the UI reads one field.
      const rawItems = Array.isArray(mi) ? mi : (mi.items || []);
      setItems(rawItems.map(i => ({ ...i, price: i.price ?? i.base_price })));
      const map = {};
      for (const i of ing) { map[i.id] = i; }
      setIngredients(map);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const computeCost = async (recipeId) => {
    setComputing(true);
    setCostResult(null);
    try {
      const params = new URLSearchParams();
      if (sellingPrice) params.set('selling_price', sellingPrice);
      const res = await fetch(
        `${API_URL}/api/costing/recipes/${recipeId}/cost?${params.toString()}`,
        { method: 'POST', headers: authHeaders }
      );
      if (res.ok) setCostResult(await res.json());
      else setCostResult({ error: (await res.json().catch(() => ({}))).detail || res.statusText });
    } catch (e) { setCostResult({ error: String(e) }); }
    finally { setComputing(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Dish list */}
      <aside className="lg:col-span-4 bg-surface border border-surface-border rounded-lg overflow-hidden" data-testid="cost-intel-dish-list">
        <div className="px-4 py-3 border-b border-surface-border flex items-center justify-between">
          <div className="font-display text-sm flex items-center gap-2"><ChefHat className="w-4 h-4" /> Dishes</div>
          <span className="text-xs text-ink-muted tabular-nums">{items.length}</span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-ink-muted">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-ink-muted text-sm" data-testid="cost-intel-empty-items">
            No menu items yet. Create one under <strong>Run Service → Menu Configuration</strong>.
          </div>
        ) : (
          <ul className="max-h-[60vh] overflow-y-auto">
            {items.map(it => (
              <li key={it.id || it._id}>
                <button
                  onClick={() => { setSelected(it); setCostResult(null); }}
                  className={`w-full text-left px-4 py-3 border-b border-surface-border last:border-0 hover:bg-surface-sunken motion-organic ${
                    selected?.id === it.id ? 'bg-primary-50' : ''
                  }`}
                  data-testid={`cost-intel-dish-${it.id}`}
                >
                  <div className="text-sm font-medium text-ink">{it.name}</div>
                  <div className="text-xs text-ink-muted mt-0.5">
                    {it.category || '—'} · {fmtMoney(it.price)}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      {/* Detail / cost result */}
      <section className="lg:col-span-8 space-y-4">
        {!selected ? (
          <div className="bg-surface border border-surface-border rounded-lg p-12 text-center" data-testid="cost-intel-no-dish-selected">
            <ChefHat className="w-10 h-10 mx-auto text-ink-subtle mb-3" />
            <p className="font-display text-lg text-ink">Pick a dish</p>
            <p className="text-sm text-ink-muted mt-1">Recipe-DAG cost, GP%, and per-ingredient provenance will appear here.</p>
          </div>
        ) : (
          <>
            <div className="bg-surface border border-surface-border rounded-lg p-5" data-testid="cost-intel-detail-card">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-overline text-ink-muted">{selected.category || 'dish'}</div>
                  <h2 className="font-display text-2xl text-ink mt-1">{selected.name}</h2>
                  <p className="text-sm text-ink-muted mt-2">Sell price {fmtMoney(selected.price)}</p>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
                <div>
                  <label className="text-xs uppercase tracking-overline text-ink-muted">Selling price for GP% (optional)</label>
                  <div className="mt-1 relative">
                    <PoundSterling className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                    <input aria-label="Selling price for GP% (optional)"
                      type="number"
                      step="0.01"
                      placeholder={selected.price ?? '0.00'}
                      value={sellingPrice}
                      onChange={(e) => setSellingPrice(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm focus:outline-none focus:border-primary-500"
                      data-testid="cost-intel-selling-price-input"
                    />
                  </div>
                </div>
                <button
                  disabled={computing}
                  onClick={() => computeCost(selected.recipe_id || selected.id)}
                  className="px-4 py-2 bg-primary-500 text-surface rounded-md text-sm hover:bg-primary-600 disabled:opacity-50 motion-organic"
                  data-testid="cost-intel-compute-btn"
                >
                  {computing ? <Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> : <TrendingUp className="w-4 h-4 inline mr-2" />}
                  Compute Cost
                </button>
              </div>
            </div>

            {/* Result */}
            {costResult ? (
              costResult.error ? (
                <div className="bg-danger/5 border border-danger/30 text-danger rounded-md p-4 text-sm flex items-start gap-2" data-testid="cost-intel-error">
                  <AlertTriangle className="w-4 h-4 mt-0.5" />
                  <div>
                    <p className="font-medium">Cost compute failed</p>
                    <p className="opacity-80 mt-0.5">{costResult.error}</p>
                    <p className="opacity-80 mt-2 text-xs">
                      Tip: this endpoint expects a costed recipe linked by ID. If this dish has no recipe yet,
                      seed one under <strong>Cut Cost → AI Ingestion</strong> (CSV) or via the costing seed scripts.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-surface border border-surface-border rounded-lg p-5" data-testid="cost-intel-result-card">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <Metric label="Total Cost"    value={fmtMoney(costResult.total)} />
                    <Metric label="Per Portion"   value={fmtMoney(costResult.per_portion)} mono />
                    <Metric label="Per Unit"      value={fmtMoney(costResult.per_unit)} mono />
                    <Metric label="GP %"          value={costResult.gp_pct ? `${(parseFloat(costResult.gp_pct) * 100).toFixed(1)}%` : '—'} mono />
                  </div>

                  {costResult.recommendation && (
                    <div className="bg-primary-50 border border-primary-500/40 rounded-md p-4 mb-4 flex items-start gap-3" data-testid="cost-intel-recommendation">
                      <TrendingUp className="w-5 h-5 text-primary-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="font-display text-sm text-ink">Raise price to protect margin</p>
                        <p className="text-sm text-ink-muted mt-1">
                          GP% is {(costResult.recommendation.current_gp_pct * 100).toFixed(1)}% — below the {(costResult.recommendation.target_gp_pct * 100).toFixed(0)}% target.
                          Raise <strong>{selected.name}</strong> from {fmtMoney(costResult.recommendation.current_price)} to{' '}
                          <strong className="text-ink">{fmtMoney(costResult.recommendation.recommended_price)}</strong> (+{fmtMoney(costResult.recommendation.delta)}).
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs uppercase tracking-overline text-ink-muted">Confidence</span>
                    <span className={`text-xs px-2 py-0.5 rounded-md border ${confidencePill(costResult.confidence)}`} data-testid="cost-intel-confidence">
                      {costResult.confidence || 'unknown'}
                    </span>
                  </div>

                  {costResult.ingredient_bases && Object.keys(costResult.ingredient_bases).length > 0 ? (
                    <div>
                      <div className="text-xs uppercase tracking-overline text-ink-muted mb-2">Provenance per ingredient</div>
                      <table className="w-full text-sm">
                        <thead className="text-xs text-ink-muted uppercase tracking-overline">
                          <tr className="border-b border-surface-border">
                            <th className="text-left py-2">Ingredient</th>
                            <th className="text-right py-2">Unit cost</th>
                            <th className="text-left py-2 pl-3">Basis</th>
                            <th className="text-right py-2">Conf.</th>
                            <th className="text-right py-2">As of</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(costResult.ingredient_bases).map(([ingId, basis]) => (
                            <tr key={ingId} className="border-b border-surface-border last:border-0" data-testid={`cost-intel-basis-row-${ingId}`}>
                              <td className="py-2 text-ink">
                                {ingredients[ingId]?.name || <code className="text-xs text-ink-subtle">{ingId.slice(0, 8)}…</code>}
                              </td>
                              <td className="py-2 text-right font-data">{fmtUnitCost(basis.value, ingredients[ingId]?.unit)}</td>
                              <td className="py-2 pl-3 text-xs text-ink-muted">{basis.basis}</td>
                              <td className="py-2 text-right font-data">{Math.round((basis.confidence || 0) * 100)}%</td>
                              <td className="py-2 text-right text-xs text-ink-muted">{basis.as_of}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-xs text-ink-muted">No per-ingredient provenance returned.</p>
                  )}
                </div>
              )
            ) : null}
          </>
        )}
      </section>
    </div>
  );
};

const Metric = ({ label, value, mono }) => (
  <div>
    <div className="text-xs uppercase tracking-overline text-ink-muted">{label}</div>
    <div className={`font-display text-2xl text-ink mt-1 ${mono ? 'tabular-nums' : ''}`}>{value}</div>
  </div>
);

// ───────────────────────────────────────────────────────────────────────────
// Menu Engineering
// ───────────────────────────────────────────────────────────────────────────
const todayIso = () => new Date().toISOString().slice(0, 10);

const daysAgoIso = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const MenuEngineering = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState(daysAgoIso(30));
  const [endDate, setEndDate] = useState(todayIso());

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const { headers: authHeaders } = useOrgScopedHeaders(token);

  const load = useCallback(async () => {
    if (!authHeaders['X-Organization-Id']) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const res = await fetch(`${API_URL}/api/costing/menu-engineering?${params.toString()}`, {
        headers: authHeaders,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || res.statusText);
      }
      setReport(await res.json());
    } catch (e) {
      setReport(null);
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [authHeaders, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const items = report?.items || [];
  const counts = items.reduce((acc, item) => {
    acc[item.quadrant] = (acc[item.quadrant] || 0) + 1;
    return acc;
  }, {});
  const ranked = [...items].sort((a, b) => {
    const aQty = parseFloat(a.quantity_sold || 0);
    const bQty = parseFloat(b.quantity_sold || 0);
    return bQty - aQty;
  });

  return (
    <div className="space-y-6" data-testid="menu-engineering-page">
      <div className="bg-surface border border-surface-border rounded-lg p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="text-xs uppercase tracking-overline text-ink-muted">Operator depth</div>
            <h2 className="font-display text-2xl text-ink mt-1 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-primary-500" />
              Menu Engineering
            </h2>
            <p className="text-sm text-ink-muted mt-1">
              Margin and popularity from closed sales, menu links, and recipe costs.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
            <div>
              <label className="text-xs uppercase tracking-overline text-ink-muted">From</label>
              <input aria-label="From"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="block w-full mt-1 px-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm focus:outline-none focus:border-primary-500"
                data-testid="menu-engineering-start-date"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-overline text-ink-muted">To</label>
              <input aria-label="To"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="block w-full mt-1 px-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm focus:outline-none focus:border-primary-500"
                data-testid="menu-engineering-end-date"
              />
            </div>
            <button
              onClick={load}
              disabled={loading}
              className="self-end px-4 py-2 bg-primary-500 text-surface rounded-md text-sm hover:bg-primary-600 disabled:opacity-50 motion-organic"
              data-testid="menu-engineering-refresh"
            >
              {loading ? <Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 inline mr-2" />}
              Refresh
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="bg-danger/5 border border-danger/30 text-danger rounded-md p-4 text-sm flex items-start gap-2" data-testid="menu-engineering-error">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <span>{error}</span>
        </div>
      ) : null}

      {loading ? (
        <div className="bg-surface border border-surface-border rounded-lg p-12 text-center text-ink-muted" data-testid="menu-engineering-loading">
          <Loader2 className="w-6 h-6 mx-auto animate-spin" />
        </div>
      ) : report ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {['star', 'workhorse', 'puzzle', 'dog', 'unclassified'].map(key => (
              <div key={key} className={`border rounded-lg p-4 ${quadrantMeta[key].tone}`} data-testid={`menu-engineering-count-${key}`}>
                <div className="text-xs uppercase tracking-overline opacity-80">{quadrantMeta[key].label}</div>
                <div className="font-display text-3xl mt-1 tabular-nums">{counts[key] || 0}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-surface border border-surface-border rounded-lg p-4">
              <Metric label="Sales Lines" value={report.sales_sample_size ?? 0} mono />
            </div>
            <div className="bg-surface border border-surface-border rounded-lg p-4">
              <Metric label="Popularity Cutoff" value={fmtPct(report.popularity_cutoff, 1)} mono />
            </div>
            <div className="bg-surface border border-surface-border rounded-lg p-4">
              <Metric label="GP Cutoff" value={fmtPct(report.gp_cutoff, 1)} mono />
            </div>
          </div>

          {report.warnings?.length ? (
            <div className="bg-warn/5 border border-warn/30 rounded-md px-4 py-3 text-sm text-warn" data-testid="menu-engineering-warnings">
              {report.warnings.map((w, i) => <div key={i}>{w}</div>)}
            </div>
          ) : null}

          <div className="bg-surface border border-surface-border rounded-lg overflow-hidden" data-testid="menu-engineering-table">
            <div className="px-4 py-3 border-b border-surface-border flex items-center justify-between">
              <div className="font-display text-sm">Menu items</div>
              <span className="text-xs text-ink-muted tabular-nums">{ranked.length}</span>
            </div>
            {ranked.length === 0 ? (
              <div className="p-10 text-center text-sm text-ink-muted" data-testid="menu-engineering-empty">
                No menu items found for this tenant.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-ink-muted uppercase tracking-overline bg-surface-sunken">
                    <tr>
                      <th className="text-left px-4 py-3">Item</th>
                      <th className="text-right px-4 py-3">Sold</th>
                      <th className="text-right px-4 py-3">Revenue</th>
                      <th className="text-right px-4 py-3">Avg price</th>
                      <th className="text-right px-4 py-3">Portion cost</th>
                      <th className="text-right px-4 py-3">GP</th>
                      <th className="text-right px-4 py-3">Popularity</th>
                      <th className="text-left px-4 py-3">Quadrant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ranked.map(item => (
                      <tr key={item.menu_item_id} className="border-t border-surface-border" data-testid={`menu-engineering-row-${item.menu_item_id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-ink">{item.name}</div>
                          <div className="text-xs text-ink-muted">
                            {item.category || '—'} · {item.recipe_id || 'no recipe'}
                          </div>
                          {item.warnings?.length ? (
                            <div className="mt-2 text-xs text-warn">
                              {item.warnings.slice(0, 2).join(' ')}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-right font-data">{item.quantity_sold}</td>
                        <td className="px-4 py-3 text-right font-data">{fmtMoney(item.revenue)}</td>
                        <td className="px-4 py-3 text-right font-data">{fmtMoney(item.avg_selling_price)}</td>
                        <td className="px-4 py-3 text-right font-data">{fmtMoney(item.per_portion_cost)}</td>
                        <td className="px-4 py-3 text-right font-data">{fmtPct(item.gp_pct, 1)}</td>
                        <td className="px-4 py-3 text-right font-data">{fmtPct(item.popularity_index, 1)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 rounded-md border text-xs ${quadrantMeta[item.quadrant]?.tone || quadrantMeta.unclassified.tone}`}>
                            {quadrantMeta[item.quadrant]?.label || item.quadrant}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};


export default CostIntelligence;
